"use client";

import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import TuneIcon from "@mui/icons-material/Tune";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import type { Orientation, StoreFilters, StoreSort } from "./artwork-store";
import { STORE_SORTS } from "./artwork-store";
import HideChatWidget from "./HideChatWidget";

type Filters = Omit<StoreFilters, "collection">;
type T = (key: string, values?: Record<string, string | number>) => string;

/**
 * The toolbar's controls for shape, colour and mood, and the order.
 *
 * Shape and mood live behind one "Filtri" button on every screen. Inline they
 * took a row of their own above the first painting, and on a phone three;
 * the toolbar is one line, and what someone narrowed by stays in view as
 * removable chips (ActiveFilterChips) under it. The sort stays outside the
 * panel, since it is the one control people reach for without wanting to
 * narrow anything.
 */

export interface FilterButtonProps {
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
  /** Only the choices some work in view actually has. */
  orientations: Orientation[];
  moodTags: string[];
  /** Works shown with the current filters, for the panel's button. */
  count: number;
  t: T;
}

/** "Filtri" and the panel it opens: a bottom sheet on a phone, a side panel on a desktop. */
export function FilterButton({ filters, onChange, orientations, moodTags, count, t }: FilterButtonProps) {
  const [open, setOpen] = useState(false);
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"), { noSsr: true });
  const active = (filters.orientation ? 1 : 0) + filters.tags.length;
  const hasGroups = orientations.length > 1 || moodTags.length > 0;
  if (!hasGroups) return null;

  const toggleTag = (tag: string) =>
    onChange({
      tags: filters.tags.includes(tag)
        ? filters.tags.filter((x) => x !== tag)
        : [...filters.tags, tag],
    });

  // A single answer per question for shape, so a second tap clears it.
  const toggleOrientation = (o: Orientation) =>
    onChange({ orientation: filters.orientation === o ? null : o });

  return (
    <>
      <Badge color="primary" badgeContent={active} invisible={!active} overlap="rectangular">
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<TuneIcon />}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          sx={{
            height: 40,
            borderColor: "divider",
            textTransform: "none",
            fontWeight: 700,
            whiteSpace: "nowrap",
            borderRadius: 999,
            px: 1.75,
          }}
        >
          {t("filters")}
        </Button>
      </Badge>

      {open && <HideChatWidget />}
      <Drawer
        anchor={desktop ? "right" : "bottom"}
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: desktop
            ? { width: 380 }
            : { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh" },
        }}
      >
        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            pb: "calc(20px + env(safe-area-inset-bottom))",
            display: "flex",
            flexDirection: "column",
            minHeight: { md: "100%" },
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: { xs: "block", md: "none" },
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: "divider",
              mx: "auto",
              mb: 2,
            }}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              {t("filters")}
            </Typography>
            <IconButton onClick={() => setOpen(false)} aria-label={t("close")} sx={{ mr: -1 }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack spacing={3}>
            {orientations.length > 1 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("filterOrientation")}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {orientations.map((o) => (
                    <Chip
                      key={o}
                      label={t(`orientations.${o}`)}
                      color={filters.orientation === o ? "primary" : "default"}
                      variant={filters.orientation === o ? "filled" : "outlined"}
                      onClick={() => toggleOrientation(o)}
                      aria-pressed={filters.orientation === o}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {moodTags.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("filterMood")}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {moodTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={t(`moodTags.${tag}`)}
                      color={filters.tags.includes(tag) ? "primary" : "default"}
                      variant={filters.tags.includes(tag) ? "filled" : "outlined"}
                      onClick={() => toggleTag(tag)}
                      aria-pressed={filters.tags.includes(tag)}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
          <Stack direction="row" spacing={1.5} sx={{ mt: { xs: 4, md: "auto" }, pt: { md: 4 } }}>
            <Button
              variant="text"
              disabled={!active}
              onClick={() => onChange({ orientation: null, tags: [] })}
              sx={{ textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {t("clearFilters")}
            </Button>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => setOpen(false)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("showResults", { count })}
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}

/**
 * The order, as a menu. A labelled button on a desktop; on a phone just the
 * icon, so the search box keeps its width.
 */
export function SortMenu({
  sort,
  onChange,
  t,
}: {
  sort: StoreSort;
  onChange: (sort: StoreSort) => void;
  t: T;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-label={`${t("sortLabel")}: ${t(`sorts.${sort}`)}`}
        sx={{
          height: 40,
          minWidth: 40,
          px: { xs: 0, md: 1.75 },
          borderColor: "divider",
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 600,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <SwapVertIcon sx={{ fontSize: 20 }} />
        <Box component="span" sx={{ display: { xs: "none", md: "inline" }, ml: 0.75 }}>
          {t(`sorts.${sort}`)}
        </Box>
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, display: "block" }}>
          {t("sortLabel")}
        </Typography>
        {STORE_SORTS.map((s) => (
          <MenuItem
            key={s}
            selected={s === sort}
            onClick={() => {
              onChange(s);
              setAnchor(null);
            }}
          >
            <ListItemIcon>{s === sort ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            {t(`sorts.${s}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** What the list is narrowed by, each with its own ×, and a way to drop them all. */
export function ActiveFilterChips({
  filters,
  onChange,
  onClearAll,
  t,
}: {
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
  onClearAll: () => void;
  t: T;
}) {
  const chips: { key: string; label: string; remove: () => void }[] = [];
  if (filters.saved) {
    chips.push({ key: "saved", label: t("wishlistFilter"), remove: () => onChange({ saved: false }) });
  }
  if (filters.orientation) {
    chips.push({
      key: "orientation",
      label: t(`orientations.${filters.orientation}`),
      remove: () => onChange({ orientation: null }),
    });
  }
  for (const tag of filters.tags) {
    chips.push({
      key: `tag-${tag}`,
      label: t(`moodTags.${tag}`),
      remove: () => onChange({ tags: filters.tags.filter((x) => x !== tag) }),
    });
  }
  if (!chips.length) return null;

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          size="small"
          label={chip.label}
          onDelete={chip.remove}
          sx={{ fontWeight: 600 }}
        />
      ))}
      {chips.length > 1 && (
        <Button size="small" onClick={onClearAll} sx={{ textTransform: "none", fontWeight: 600 }}>
          {t("clearAll")}
        </Button>
      )}
    </Stack>
  );
}
